import { getOrCreateShortCode, getVehicleIdFromShortCode } from './shortCode';

/**
 * Normalizes a string for URL slugs
 */
const normalizeForSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generates a URL-friendly slug for a vehicle based on type
 * 
 * Type 1 (Carro): /estoque/carro/marca-modelo-versao-cor-ano-{code}
 * Type 2 (Moto): /estoque/moto/marca-modelo-cor-ano-{code}
 * Type 3 (Caminhão): /estoque/caminhao/marca-modelo-versao-cor-ano-{code}
 */
export const generateVehicleSlug = async (vehicle: any): Promise<string> => {
  const brand = normalizeForSlug(vehicle.brand);
  const model = normalizeForSlug(vehicle.model);
  const version = normalizeForSlug(vehicle.version || '');
  const color = normalizeForSlug(vehicle.color || '');
  const year = vehicle.modelYear;
  const shortCode = await getOrCreateShortCode(vehicle.id);
  
  // Get vehicle type path
  let typePath = 'carro';
  switch (vehicle.type) {
    case 1:
      typePath = 'carro';
      break;
    case 2:
      typePath = 'moto';
      break;
    case 3:
      typePath = 'caminhao';
      break;
  }
  
  // Build slug based on vehicle type
  let slug = '';
  
  if (vehicle.type === 1) {
    // Carro: marca-modelo-versao-cor-ano-{code}
    slug = [brand, model, version, color, year, shortCode]
      .filter(Boolean)
      .join('-');
  } else if (vehicle.type === 2) {
    // Moto: marca-modelo-cor-ano-{code}
    slug = [brand, model, color, year, shortCode]
      .filter(Boolean)
      .join('-');
  } else if (vehicle.type === 3) {
    // Caminhão: marca-modelo-versao-cor-ano-{code}
    slug = [brand, model, version, color, year, shortCode]
      .filter(Boolean)
      .join('-');
  } else {
    // Fallback
    slug = [brand, model, year, shortCode]
      .filter(Boolean)
      .join('-');
  }
  
  return `${typePath}/${slug}`;
};

/**
 * Extracts the vehicle ID from a slug
 * @param slug - The slug (can be with or without type prefix)
 * Examples:
 *   - carro/toyota-corolla-xei-branco-2025-l12ab
 *   - moto/honda-cb-500-preto-2024-m8x3k
 *   - caminhao/volvo-fh-540-branco-2023-a5n7r
 * @returns The vehicle ID or null if invalid
 */
export const extractIdFromSlug = async (slug: string): Promise<string | null> => {
  // Remove type prefix if present (carro/, moto/, caminhao/)
  const slugWithoutType = slug.replace(/^(carro|moto|caminhao)\//, '');
  
  // The short code is always the last part after the last hyphen
  const parts = slugWithoutType.split('-');
  const shortCode = parts[parts.length - 1];
  
  // Try to get vehicle ID from short code (5 characters)
  if (shortCode && /^[a-z0-9]{5}$/.test(shortCode)) {
    const vehicleId = await getVehicleIdFromShortCode(shortCode);
    if (vehicleId) {
      return vehicleId;
    }
  }
  
  // Fallback: check if it's an old 10-character code
  if (shortCode && /^[a-z0-9]{10}$/.test(shortCode)) {
    const vehicleId = await getVehicleIdFromShortCode(shortCode);
    if (vehicleId) {
      return vehicleId;
    }
  }
  
  // Fallback: check if it's a direct ID (for backward compatibility)
  if (shortCode && (/^\d+$/.test(shortCode) || /^[A-F0-9-]{36}$/i.test(shortCode))) {
    return shortCode;
  }
  
  return null;
};

